import { useAuthUser } from "react-auth-kit";
import { buildDeleteOptions, buildGetOptions, buildPostOptions, fetchWithErrorHandling } from "../FetchUtils";
import { getRegistryURL } from "./Registry";
import { AppContext } from "../../utils/AppContext";
import React from "react";

export enum MaintenanceStatus {
  SCHEDULED = "SCHEDULED",
  IN_PROGRESS = "IN_PROGRESS",
  COMPLETED = "COMPLETED",
}

export interface IMaintenanceFile {
  status?: MaintenanceStatus;
  maintenanceStartedAt?: number; // Unix timestamp in seconds
  maintenanceEndsAt?: number; // Unix timestamp in seconds
  defaultMessage?: string;
  localizedMessages?: {
    [locale: string]: string; // For example: "en-US" => "The system is under maintenance."
  };
}

export const useMaintenanceService = () => {
  const authUser = useAuthUser();
  const context = React.useContext(AppContext);
  const selectedEnvironment = context?.selectedEnvironment;

  const resolve = (path: string) => {
    return new URL(`/v1/maintenance${path}`, getRegistryURL(selectedEnvironment)).toString();
  };

  return {
    getMaintenanceMessage: () => {
      const endpoint = resolve("/message");
      return fetchWithErrorHandling(endpoint, buildGetOptions(authUser()?.access_token));
    },
    updateMaintenanceMessage: (maintenanceFile: IMaintenanceFile) => {
      const endpoint = resolve("/message");
      return fetchWithErrorHandling(endpoint, buildPostOptions(authUser()?.access_token, { ...maintenanceFile }));
    },
    listMaintenanceTemplates: () => {
      const endpoint = resolve("/templates");
      return fetchWithErrorHandling(endpoint, buildGetOptions(authUser()?.access_token));
    },
    getMaintenanceTemplate: (name: string) => {
      const endpoint = resolve(`/templates/${name}`);
      return fetchWithErrorHandling(endpoint, buildGetOptions(authUser()?.access_token));
    },
    createMaintenanceTemplate: (
      name: string,
      defaultMessage: string,
      localizedMessages: {
        [locale: string]: string;
      },
    ) => {
      const endpoint = resolve("/templates");
      return fetchWithErrorHandling(
        endpoint,
        buildPostOptions(authUser()?.access_token, { name, defaultMessage, localizedMessages }),
      );
    },
    updateMaintenanceTemplate: (
      name: string,
      defaultMessage: string,
      localizedMessages: {
        [locale: string]: string;
      },
    ) => {
      const endpoint = resolve(`/templates/${name}`);
      return fetchWithErrorHandling(
        endpoint,
        buildPostOptions(authUser()?.access_token, { defaultMessage, localizedMessages }),
      );
    },
    deleteMaintenanceTemplate: (name: string) => {
      const endpoint = resolve(`/templates/${name}`);
      return fetchWithErrorHandling(endpoint, buildDeleteOptions(authUser()?.access_token));
    },
    startMaintenance: (envName?: string) => {
      const endpoint = resolve("/start");
      const payload = envName ? { envName } : {};
      return fetch(endpoint, buildPostOptions(authUser()?.access_token, payload));
    },
    endMaintenance: (envName?: string) => {
      const endpoint = resolve("/end");
      const payload = envName ? { envName } : {};
      return fetch(endpoint, buildPostOptions(authUser()?.access_token, payload));
    },
  };
};
